import axios, { AxiosInstance, AxiosResponse } from 'axios'
import {
	AssignInterviewerToCandidatesDto,
	BatchCandidateDetailResponse,
	BulkInterviewAssignmentDto,
	BulkTeamAssignmentDto,
	Candidate,
	CreateCycleDto,
	CreateCycleFormAssignmentData,
	CreateFormDefinitionData,
	CreateFormTemplateData,
	CreateInterviewAssignmentDto,
	CreateUserDto,
	Cycle,
	CycleFormAssignment,
	CycleStatement,
	CycleStatusResponseDto,
	FormDefinition,
	FormTemplate,
	InterviewAssignment,
	LoginRequest,
	LoginResponse,
	SharedParameter,
	Station,
	StatementTemplate,
	SubmissionTrackerResponseDto,
	TeamAssignment,
	TeamStatusDto,
	UpdateCandidateStatusDto,
	User,
	RegistrationState,
	SaveWeightsBody,
} from '../types/api'
import { normalizeCandidateDetailBatch, normalizeCycleStatus } from '../utils/apiNormalization'

const getBaseURL = (): string =>
	process.env.REACT_APP_API_BASE_URL || 'https://api.opselect.xyz/api'

class ApiService {
	private api: AxiosInstance
	private token: string | null = null

	constructor() {
		const baseURL = getBaseURL()

		this.api = axios.create({
			baseURL,
			headers: {
				'Content-Type': 'application/json'
			}
		})

		this.api.interceptors.request.use(config => {
			if (this.token) {
				config.headers.Authorization = `Bearer ${this.token}`
			}
			return config
		})

		this.api.interceptors.response.use(
			response => response,
			error => {
				if (error.response?.status === 401) {
					this.clearToken()
					window.location.href = '/login'
				} else if (error.response?.status === 403) {
					// Add custom property to distinguish 403 errors
					error.isPermissionDenied = true
				}
				return Promise.reject(error)
			}
		)

		const savedToken = localStorage.getItem('token')
		if (savedToken) {
			this.token = savedToken
		}
	}

	setToken(token: string) {
		this.token = token
		localStorage.setItem('token', token)
	}

	clearToken() {
		this.token = null
		localStorage.removeItem('token')
		localStorage.removeItem('user')
	}

	async login(credentials: LoginRequest): Promise<LoginResponse> {
		const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', credentials)
		const { access_token, user } = response.data
		this.setToken(access_token)
		localStorage.setItem('user', JSON.stringify(user))
		return response.data
	}


	// Users endpoints
	async getUsers(): Promise<User[]> {
		const response: AxiosResponse<User[]> = await this.api.get('/users')
		return response.data
	}

	async getUser(id: string): Promise<User> {
		const response: AxiosResponse<User> = await this.api.get(`/users/${id}`)
		return response.data
	}

	async createUser(userData: CreateUserDto): Promise<User> {
		const response: AxiosResponse<User> = await this.api.post('/users', userData)
		return response.data
	}

	async updateUser(id: string, userData: Partial<CreateUserDto>): Promise<User> {
		const response: AxiosResponse<User> = await this.api.patch(`/users/${id}`, userData)
		return response.data
	}

	async deleteUser(id: string): Promise<void> {
		await this.api.delete(`/users/${id}`)
	}

	// Cycles endpoints
	async getCycles(): Promise<Cycle[]> {
		const response: AxiosResponse<Cycle[]> = await this.api.get('/cycles')
		return response.data
	}

	async getCycle(id: string): Promise<Cycle> {
		const response: AxiosResponse<Cycle> = await this.api.get(`/cycles/${id}`)
		return response.data
	}

	async createCycle(cycleData: CreateCycleDto): Promise<Cycle> {
		const response: AxiosResponse<Cycle> = await this.api.post('/cycles', cycleData)
		return response.data
	}

	async updateCycle(id: string, cycleData: Partial<CreateCycleDto>): Promise<Cycle> {
		const response: AxiosResponse<Cycle> = await this.api.patch(`/cycles/${id}`, cycleData)
		return response.data
	}

	async deleteCycle(id: string): Promise<void> {
		await this.api.delete(`/cycles/${id}`)
	}

	async closeCycle(id: string): Promise<void> {
		await this.api.post(`/cycles/${id}/close`)
	}

	async getCycleRegistration(cycleId: string): Promise<RegistrationState> {
		const response = await this.api.get<RegistrationState>(`/cycles/${cycleId}/registration`)
		return response.data
	}

	async openCycleRegistration(
		cycleId: string,
		body: { closesAt?: string; cap?: number },
	): Promise<RegistrationState> {
		const response = await this.api.post<RegistrationState>(`/cycles/${cycleId}/registration/open`, body)
		return response.data
	}

	async closeCycleRegistration(cycleId: string): Promise<RegistrationState> {
		const response = await this.api.post<RegistrationState>(`/cycles/${cycleId}/registration/close`)
		return response.data
	}

	async getUnitInfo(): Promise<{ message: string; unitId: string; userId: string; email: string }> {
		const response = await this.api.get<{
			message: string
			unitId: string
			userId: string
			email: string
		}>('/cycles/my-unit/info')
		return response.data
	}

	// Candidates endpoints
	async getCycleStatus(cycleId: string): Promise<CycleStatusResponseDto> {
		const response = await this.api.get(`/candidates/cycle/${cycleId}/status`)
		return normalizeCycleStatus(response.data)
	}

	async getSubmissionTracker(cycleId: string): Promise<SubmissionTrackerResponseDto> {
		const response = await this.api.get(`/submission-tracker/cycle/${cycleId}`)
		return response.data
	}

	async updateCandidateStatus(
		candidateId: string,
		statusData: UpdateCandidateStatusDto
	): Promise<Candidate> {
		const response: AxiosResponse<Candidate> = await this.api.patch(
			`/candidates/${candidateId}/status`,
			statusData
		)
		return response.data
	}

	async getInactivationReasons(): Promise<{ values: string[] }> {
		const response = await this.api.get('/candidates/inactivation-reasons')
		return response.data
	}

	async uploadCandidatesCSV(
		cycleId: string,
		file: File
	): Promise<{ count: number; message: string }> {
		const formData = new FormData()
		formData.append('file', file)

		const response = await this.api.post(`/candidates/cycle/${cycleId}/csv`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
		return response.data
	}

	// CSV Upload methods
	async uploadUserTeamCSV(
		cycleId: string,
		file: File
	): Promise<{ count: number; message: string }> {
		const formData = new FormData()
		formData.append('file', file)

		const response = await this.api.post(`/users/cycle/${cycleId}/teams/csv`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
		return response.data
	}

	async uploadInterviewsCSV(
		cycleId: string,
		file: File
	): Promise<{ count: number; message: string }> {
		const formData = new FormData()
		formData.append('file', file)

		const response = await this.api.post(`/users/cycle/${cycleId}/interviews/csv`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
		return response.data
	}

	// Team Assignment endpoints
	async assignToTeams(
		cycleId: string,
		assignments: BulkTeamAssignmentDto
	): Promise<TeamAssignment[]> {
		const response = await this.api.post(`/cycles/${cycleId}/team-assignments/assign`, assignments)
		return response.data
	}

	async getTeamAssignments(cycleId: string): Promise<TeamAssignment[]> {
		const response = await this.api.get(`/cycles/${cycleId}/team-assignments`)
		return response.data
	}

	async getMyTeamStatus(cycleId: string): Promise<TeamStatusDto | null> {
		const response = await this.api.get(`/cycles/${cycleId}/team-assignments/my-team/status`)
		return response.data
	}

	// Interview Assignment endpoints
	async createInterviewAssignment(
		cycleId: string,
		assignment: CreateInterviewAssignmentDto
	): Promise<InterviewAssignment> {
		const response = await this.api.post(`/cycles/${cycleId}/interview-assignments`, assignment)
		return response.data
	}

	async createBulkInterviewAssignments(
		cycleId: string,
		assignments: BulkInterviewAssignmentDto
	): Promise<InterviewAssignment[]> {
		const response = await this.api.post(
			`/cycles/${cycleId}/interview-assignments/bulk`,
			assignments
		)
		return response.data
	}

	async assignInterviewerToCandidates(
		cycleId: string,
		assignment: AssignInterviewerToCandidatesDto
	): Promise<InterviewAssignment[]> {
		const response = await this.api.post(
			`/cycles/${cycleId}/interview-assignments/assign-interviewer`,
			assignment
		)
		return response.data
	}

	async getInterviewAssignmentsByCycle(cycleId: string): Promise<InterviewAssignment[]> {
		const response = await this.api.get(`/cycles/${cycleId}/interview-assignments`)
		return response.data
	}

	async getInterviewAssignmentsByInterviewer(
		cycleId: string,
		userId: string
	): Promise<InterviewAssignment[]> {
		const response = await this.api.get(
			`/cycles/${cycleId}/interview-assignments/interviewer/${userId}`
		)
		return response.data
	}

	async getMyInterviews(cycleId: string): Promise<InterviewAssignment[]> {
		const response = await this.api.get(`/cycles/${cycleId}/interview-assignments/my-interviews`)
		return response.data
	}

	async getInterviewAssignmentsByCandidate(
		cycleId: string,
		candidateId: string
	): Promise<InterviewAssignment[]> {
		const response = await this.api.get(
			`/cycles/${cycleId}/interview-assignments/candidate/${candidateId}`
		)
		return response.data
	}

	async removeInterviewAssignment(cycleId: string, assignmentId: string): Promise<void> {
		await this.api.delete(`/cycles/${cycleId}/interview-assignments/${assignmentId}`)
	}

	async removeInterviewAssignmentsByInterviewer(cycleId: string, userId: string): Promise<void> {
		await this.api.delete(`/cycles/${cycleId}/interview-assignments/interviewer/${userId}`)
	}

	async removeInterviewAssignmentsByCandidate(cycleId: string, candidateId: string): Promise<void> {
		await this.api.delete(`/cycles/${cycleId}/interview-assignments/candidate/${candidateId}`)
	}

	// Form Definitions endpoints (legacy — kept for backward compatibility)
	async getFormDefinitionsByCycle(cycleId: string): Promise<FormDefinition[]> {
		const response: AxiosResponse<FormDefinition[]> = await this.api.get(
			`/form-definitions/cycle/${cycleId}`
		)
		return response.data
	}

	async createFormDefinition(data: CreateFormDefinitionData): Promise<FormDefinition> {
		const response: AxiosResponse<FormDefinition> = await this.api.post('/form-definitions', data)
		return response.data
	}

	async updateFormDefinition(id: string, data: Partial<CreateFormDefinitionData>): Promise<FormDefinition> {
		const response: AxiosResponse<FormDefinition> = await this.api.patch(`/form-definitions/${id}`, data)
		return response.data
	}

	async deleteFormDefinition(id: string): Promise<void> {
		await this.api.delete(`/form-definitions/${id}`)
	}

	// Form Templates endpoints
	async getFormTemplates(): Promise<FormTemplate[]> {
		const response: AxiosResponse<FormTemplate[]> = await this.api.get('/form-templates')
		return response.data
	}

	async createFormTemplate(data: CreateFormTemplateData): Promise<FormTemplate> {
		const response: AxiosResponse<FormTemplate> = await this.api.post('/form-templates', data)
		return response.data
	}

	async updateFormTemplate(id: string, data: Partial<CreateFormTemplateData>): Promise<FormTemplate> {
		const response: AxiosResponse<FormTemplate> = await this.api.patch(`/form-templates/${id}`, data)
		return response.data
	}

	async deleteFormTemplate(id: string): Promise<void> {
		await this.api.delete(`/form-templates/${id}`)
	}

	// Cycle Form Assignment endpoints
	async getFormAssignmentsByCycle(cycleId: string): Promise<CycleFormAssignment[]> {
		const response: AxiosResponse<CycleFormAssignment[]> = await this.api.get(
			`/form-templates/cycle/${cycleId}`
		)
		return response.data
	}

	async createFormAssignment(data: CreateCycleFormAssignmentData): Promise<CycleFormAssignment> {
		const response: AxiosResponse<CycleFormAssignment> = await this.api.post(
			'/form-templates/assignments',
			data
		)
		return response.data
	}

	async updateFormAssignment(
		id: string,
		data: Partial<CreateCycleFormAssignmentData>
	): Promise<CycleFormAssignment> {
		const response: AxiosResponse<CycleFormAssignment> = await this.api.patch(
			`/form-templates/assignments/${id}`,
			data
		)
		return response.data
	}

	async deleteFormAssignment(id: string): Promise<void> {
		await this.api.delete(`/form-templates/assignments/${id}`)
	}

	// Parameters bank endpoints
	async getParameters(): Promise<SharedParameter[]> {
		const response: AxiosResponse<SharedParameter[]> = await this.api.get('/parameters')
		return response.data
	}

	async createParameter(data: { name: string; description?: string; maxGrade: number }): Promise<SharedParameter> {
		const response: AxiosResponse<SharedParameter> = await this.api.post('/parameters', data)
		return response.data
	}

	async updateParameter(
		id: string,
		data: { name?: string; description?: string | null; maxGrade?: number },
	): Promise<SharedParameter> {
		const response: AxiosResponse<SharedParameter> = await this.api.patch(`/parameters/${id}`, data)
		return response.data
	}

	// Stations endpoints
	async getBankStations(): Promise<Station[]> {
		const response: AxiosResponse<Station[]> = await this.api.get('/stations/bank')
		return response.data
	}

	async getAllStations(): Promise<Station[]> {
		const response: AxiosResponse<Station[]> = await this.api.get('/stations')
		return response.data
	}

	async saveCycleWeights(cycleId: string, body: SaveWeightsBody): Promise<void> {
		await this.api.put(`/cycles/${cycleId}/weights`, body)
	}

	async getStationsByCycle(cycleId: string): Promise<Station[]> {
		const response: AxiosResponse<Station[]> = await this.api.get(`/stations/cycle/${cycleId}`)
		return response.data
	}

	async createStation(data: Partial<Station>): Promise<Station> {
		const response: AxiosResponse<Station> = await this.api.post('/stations', data)
		return response.data
	}

	async updateStation(id: string, data: Partial<Station>): Promise<Station> {
		const response: AxiosResponse<Station> = await this.api.patch(`/stations/${id}`, data)
		return response.data
	}

	async deleteStation(id: string): Promise<void> {
		await this.api.delete(`/stations/${id}`)
	}

	/** Copy stations (e.g. shared legacy rows) into the caller's own bank; returns the owned clones. */
	async cloneBankStations(stationIds: string[]): Promise<Station[]> {
		const response: AxiosResponse<Station[]> = await this.api.post('/stations/bank/clone', { stationIds })
		return response.data
	}

	async cloneStationsIntoCycle(cycleId: string, stationIds: string[]): Promise<Station[]> {
		const response = await this.api.post(`/stations/cycle/${cycleId}/clone`, { stationIds })
		return response.data
	}

	// Statements endpoints (P2)
	async getStatementBank(): Promise<StatementTemplate[]> {
		const response: AxiosResponse<StatementTemplate[]> = await this.api.get('/statements')
		return response.data
	}

	async createStatement(data: Pick<StatementTemplate, 'text' | 'tone' | 'sharedParameterId'>): Promise<StatementTemplate> {
		const response: AxiosResponse<StatementTemplate> = await this.api.post('/statements', data)
		return response.data
	}

	async updateStatement(
		id: string,
		data: Partial<Pick<StatementTemplate, 'text' | 'tone' | 'sharedParameterId'>>
	): Promise<StatementTemplate> {
		const response: AxiosResponse<StatementTemplate> = await this.api.patch(`/statements/${id}`, data)
		return response.data
	}

	async deleteStatement(id: string): Promise<void> {
		await this.api.delete(`/statements/${id}`)
	}

	async getStatementsByCycle(cycleId: string): Promise<CycleStatement[]> {
		const response: AxiosResponse<CycleStatement[]> = await this.api.get(`/statements/cycle/${cycleId}`)
		return response.data
	}

	async assignStatementsToCycle(cycleId: string, templateIds: string[]): Promise<CycleStatement[]> {
		const response: AxiosResponse<CycleStatement[]> = await this.api.post(`/statements/cycle/${cycleId}`, { templateIds })
		return response.data
	}

	async removeStatementFromCycle(cycleId: string, assignmentId: string): Promise<void> {
		await this.api.delete(`/statements/cycle/${cycleId}/${assignmentId}`)
	}

	async getCandidateDetailsBatch(ids: string[]): Promise<BatchCandidateDetailResponse> {
		const response = await this.api.post('/candidates/details/batch', { ids })
		return normalizeCandidateDetailBatch(response.data)
	}

	isAuthenticated(): boolean {
		return !!this.token
	}

	getToken(): string | null {
		return this.token
	}

	getCurrentUser(): User | null {
		const userData = localStorage.getItem('user')
		return userData ? JSON.parse(userData) : null
	}
}

export const apiService = new ApiService()
export default apiService
